import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './StoringMelden.css';
import api from '../services/api';

const StoringMelden = () => {
  const { gebruiker, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    titel: '',
    beschrijving: '',
    locatie_adres: '',
    prioriteit: 'normaal'
  });
  const [locatie, setLocatie] = useState({ lat: null, lng: null });
  const [selectedFotos, setSelectedFotos] = useState([]);
  const [fotoPreviews, setFotoPreviews] = useState([]);
  const [gpsLoading, setGpsLoading] = useState(false);

  const handleFotoChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFotos([...selectedFotos, ...files]);
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFotoPreviews(prev => [...prev, { file, preview: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFoto = (index) => {
    setSelectedFotos(selectedFotos.filter((_, i) => i !== index));
    setFotoPreviews(fotoPreviews.filter((_, i) => i !== index));
  };

  const getHuidigeLocatie = () => {
    setGpsLoading(true);
    if (!navigator.geolocation) {
      alert('Geolocatie wordt niet ondersteund door je browser');
      setGpsLoading(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        setLocatie({ lat, lng });
        
        // Reverse geocoding met OpenStreetMap Nominatim (gratis!)
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
            {
              headers: {
                'User-Agent': 'WOMSYS Werkorder App'
              }
            }
          );
          const data = await response.json();
          
          if (data && data.address) {
            const address = data.address;
            // Bouw adres op met straat + huisnummer samen
            const straat = address.road || address.street || '';
            const huisnummer = address.house_number || '';
            const straatnaam = straat && huisnummer ? `${straat} ${huisnummer}` : straat;
            
            const parts = [
              straatnaam,
              address.postcode,
              address.city || address.town || address.village
            ].filter(Boolean);
            
            const adresString = parts.join(', ');
            setFormData({ ...formData, locatie_adres: adresString });
          }
        } catch (error) {
          console.log('Adres ophalen niet gelukt, maar GPS locatie is opgeslagen');
          setFormData({ ...formData, locatie_adres: `GPS: ${lat.toFixed(6)}, ${lng.toFixed(6)}` });
        }
        
        setGpsLoading(false);
      },
      (error) => {
        alert('Kon locatie niet ophalen: ' + error.message);
        setGpsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.titel || !formData.beschrijving) {
      alert('Titel en beschrijving zijn verplicht');
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append('titel', formData.titel);
      data.append('beschrijving', formData.beschrijving);
      data.append('locatie_adres', formData.locatie_adres);
      data.append('locatie_lat', locatie.lat || '');
      data.append('locatie_lng', locatie.lng || '');
      data.append('prioriteit', formData.prioriteit);
      
      selectedFotos.forEach(foto => {
        data.append('fotos', foto);
      });

      await api.post('/storingen', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      alert('✅ Storing succesvol gemeld!');
      navigate('/dashboard');
    } catch (error) {
      alert(error.response?.data?.message || 'Fout bij aanmaken storing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="storing-melden">

      <div className="container">
        <div className="header">
          <button onClick={() => navigate(-1)} className="btn-back">← Terug</button>
          <h1>🚨 Storing Melden</h1>
        </div>

        <form onSubmit={handleSubmit} className="storing-form">
          <div className="form-group">
            <label>Titel *</label>
            <input
              type="text"
              value={formData.titel}
              onChange={(e) => setFormData({ ...formData, titel: e.target.value })}
              placeholder="Korte omschrijving van de storing"
              required
            />
          </div>

          <div className="form-group">
            <label>Beschrijving *</label>
            <textarea
              value={formData.beschrijving}
              onChange={(e) => setFormData({ ...formData, beschrijving: e.target.value })}
              placeholder="Gedetailleerde beschrijving van de storing..."
              rows="5"
              required
            />
          </div>

          <div className="form-group">
            <label>Prioriteit</label>
            <select
              value={formData.prioriteit}
              onChange={(e) => setFormData({ ...formData, prioriteit: e.target.value })}
            >
              <option value="laag">Laag</option>
              <option value="normaal">Normaal</option>
              <option value="hoog">Hoog</option>
              <option value="urgent">🚨 Urgent</option>
            </select>
          </div>

          <div className="form-group">
            <label>Locatie</label>
            <div className="locatie-input">
              <input
                type="text"
                value={formData.locatie_adres}
                onChange={(e) => setFormData({ ...formData, locatie_adres: e.target.value })}
                placeholder="Adres of locatie omschrijving"
              />
              <button
                type="button"
                onClick={getHuidigeLocatie}
                className="btn-gps"
                disabled={gpsLoading}
              >
                {gpsLoading ? '⌛ Laden...' : '📍 Gebruik huidige locatie'}
              </button>
            </div>
            {locatie.lat && (
              <small className="gps-info">
                ✅ GPS locatie opgeslagen ({locatie.lat.toFixed(6)}, {locatie.lng.toFixed(6)})
              </small>
            )}
          </div>

          <div className="form-group">
            <label>Foto's</label>
            <div className="foto-upload">
              <label className="btn-foto">
                📷 Selecteer foto's
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFotoChange}
                  style={{ display: 'none' }}
                />
              </label>
              <label className="btn-foto">
                📸 Maak foto
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFotoChange}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
            
            {fotoPreviews.length > 0 && (
              <div className="foto-previews">
                {fotoPreviews.map((item, index) => (
                  <div key={index} className="foto-preview">
                    <img src={item.preview} alt="Preview" />
                    <button
                      type="button"
                      onClick={() => removeFoto(index)}
                      className="btn-remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" onClick={() => navigate(-1)} className="btn-cancel">
              Annuleren
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? '⌛ Verzenden...' : '🚨 Storing Melden'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StoringMelden;
